import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

/** A single clickable card inside a section — same shape the reports grid uses. */
interface AdminCard {
  id?: string;
  title: string;
  icon?: string;
  navigateTo?: string;
  externalUrl?: string;
  disabled?: boolean;
}

/** A System Admin sub-page. The sub-headers themselves live in the top bar
 *  (see the custom side-navbar); this config only supplies each section's
 *  card content, keyed by the same id the route resolves to. */
interface AdminSection {
  id: string;
  title: string;
  /** Cards rendered immediately for this section. */
  cards?: AdminCard[];
  /** Optional dummy API (mock JSON) — its length drives the card count. */
  dataUrl?: string;
}

interface SystemAdminConfig {
  sections?: AdminSection[];
}

@Component({
  selector: 'app-system-admin',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './system-admin.component.html',
  styleUrl: './system-admin.component.scss',
})
export class SystemAdminComponent implements OnInit, OnDestroy {
  private http = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  private readonly DEFAULT_CONFIG_FILE = 'system-admin-config.json';
  private readonly DEFAULT_ICON = 'dashboard';

  /** Default sections mirror the top-bar sub-headers so each sub-page renders
   *  (as an empty state) even with no external config. Override via the
   *  config file to attach real cards / dummy-API URLs per section. */
  private readonly DEFAULT_SECTIONS: AdminSection[] = [
    { id: 'model-management', title: 'Model Management' },
    { id: 'entity-management', title: 'Entity Management' },
    { id: 'business-rules', title: 'Business Rules' },
    { id: 'derived-hierarchy', title: 'Derived Hierarchy' },
    { id: 'functions-permission', title: 'Functions Permission' },
    { id: 'model-permission', title: 'Model Permission' },
    { id: 'hierarchy-permission', title: 'Hierarchy Permission' },
    { id: 'transactions', title: 'Transactions' },
    { id: 'settings', title: 'Settings' },
  ];

  private sections: AdminSection[] = this.DEFAULT_SECTIONS;
  activeSection: AdminSection | null = null;

  isLoading = true;
  cards: AdminCard[] = [];

  /** Per-section card cache so a section's data is fetched at most once. */
  private cardCache = new Map<string, AdminCard[]>();
  private paramSub?: Subscription;

  ngOnInit(): void {
    const configFile =
      (this.route.snapshot.data?.['configFile'] as string)?.trim() ||
      this.DEFAULT_CONFIG_FILE;

    this.http
      .get<SystemAdminConfig>(`assets/dashboard-config/${configFile}`)
      .subscribe({
        next: (config) => this.applyConfig(config),
        error: () => this.applyConfig(null),
      });
  }

  ngOnDestroy(): void {
    this.paramSub?.unsubscribe();
  }

  private applyConfig(config: SystemAdminConfig | null): void {
    const sections = config?.sections?.length
      ? config.sections
      : this.DEFAULT_SECTIONS;
    this.sections = sections.filter((s) => !!s?.id && !!s?.title);

    // React to the :section route param so the top-bar tabs drive the page.
    this.paramSub = this.route.paramMap.subscribe((params) => {
      const id = (params.get('section') || '').trim();
      this.activateSection(id);
    });
  }

  private activateSection(id: string): void {
    const section =
      this.sections.find((s) => s.id === id) ?? this.sections[0] ?? null;
    this.activeSection = section;
    this.isLoading = false;
    if (section) this.loadSectionCards(section);
    else this.cards = [];
  }

  /** Resolves a section's cards: cache → inline cards → dummy API. */
  private loadSectionCards(section: AdminSection): void {
    const cached = this.cardCache.get(section.id);
    if (cached) {
      this.cards = cached;
      return;
    }

    if (section.cards?.length) {
      this.cardCache.set(section.id, section.cards);
      this.cards = section.cards;
      return;
    }

    const dataUrl = (section.dataUrl || '').trim();
    if (!dataUrl) {
      this.cardCache.set(section.id, []);
      this.cards = [];
      return;
    }

    this.cards = [];
    this.http.get<AdminCard[]>(dataUrl).subscribe({
      next: (data) => {
        const list = Array.isArray(data) ? data : [];
        // Guard against a stale response after the user switched sections.
        if (this.activeSection !== section) return;
        this.cardCache.set(section.id, list);
        this.cards = list;
      },
      error: () => {
        if (this.activeSection !== section) return;
        this.cardCache.set(section.id, []);
        this.cards = [];
      },
    });
  }

  iconFor(card: AdminCard): string {
    return (card.icon || '').trim() || this.DEFAULT_ICON;
  }

  onCardClick(card: AdminCard): void {
    if (card?.disabled) return;

    const route = (card.navigateTo || '').trim();
    if (route) {
      this.router.navigateByUrl(route);
      return;
    }

    const externalUrl = (card.externalUrl || '').trim();
    if (externalUrl) {
      window.open(externalUrl, '_self');
    }
  }

  onCardKeydown(event: KeyboardEvent, card: AdminCard): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.onCardClick(card);
  }

  trackByCard(index: number, card: AdminCard): string {
    return card.id ?? card.title ?? String(index);
  }
}
